import { useState, useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { Checkbox, Typography, FormControlLabel } from '@mui/material';
import { GridSortModel } from '@mui/x-data-grid';

import ReceiptsDetailsModal from './ReceiptsDetailsModal';
import { callRestGetEndpoint } from '@/common/utils/api';
import RefundModal from '../Refunds/RefundModal';
import { RestoreDataGrid } from '@/RestoreComponent/DataGrid';
import {
  getOrderBy,
  mergeCommonGridColConfig,
  PAGE_SIZES,
  renderDateTimeFromCol
} from '@/RestoreComponent/DataGrid/utils';
import ReceiptActionMenu, { RECEIPT_ACTIONS } from './ReceiptActionMenu';
import { currencyFormat } from '../Refunds/utils';
import { EmailReceiptModal } from './EmailReceiptModal';

import './index.scss';

// fn to formulate query params, best to have this outside of a cmp.render()
const getQueryParams = (
  customerAllData,
  sortOptions,
  pageSize,
  page,
  pageInfo,
  showRefundsOnly
) => {
  const params = {
    customer: customerAllData?.id,
    orderBy: getOrderBy(sortOptions),
    first: pageSize,
    showRefundsOnly: showRefundsOnly
  };
  if (page != 0 && pageInfo?.endCursor) {
    params['after'] = pageInfo.endCursor;
  }
  return params;
};

const Receipts = props => {
  const { customerAllData } = props;

  const { enqueueSnackbar } = useSnackbar();

  // datagrid state:
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const initialSort: GridSortModel = [{ field: 'created_at', sort: 'asc' }];
  const [sortOptions, setSortOptions] = useState(initialSort);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0] || 0);
  const [pageInfo, setPageInfo] = useState({
    endCursor: undefined
  });

  const [showRefundsOnly, setShowRefundsOnly] = useState(false);
  const [purchases, setPurchases] = useState([]);
  const [receiptId, setReceiptId] = useState<number | null>(null);
  const [isReceiptDetailModalOpen, setIsReceiptDetailModalOpen] =
    useState(false);
  const [isRefundInterfaceOpen, setIsRefundInterfaceOpen] = useState(false);
  const [emailReceiptModalOpen, setEmailReceiptModalOpen] = useState(false);

  const getCustomerPurchases = async () => {
    setLoading(true);
    try {
      const res = await callRestGetEndpoint(
        getQueryParams(
          customerAllData,
          sortOptions,
          pageSize,
          page,
          pageInfo,
          showRefundsOnly
        ),
        '/pos_retail2/get_all_purchases/'
      );
      setPurchases(res?.data);
      setTotalCount(res?.totalCount);
      setPageInfo(res?.pageInfo);
    } catch (e) {
      enqueueSnackbar('A problem was encountered fetching receipts.', {
        variant: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCustomerPurchases();
  }, [customerAllData, showRefundsOnly, sortOptions, pageSize, page]);

  const actionCallback = (
    purchaseId: number,
    action: keyof typeof RECEIPT_ACTIONS
  ) => {
    setReceiptId(purchaseId);
    if (action === RECEIPT_ACTIONS.VIEW) {
      setIsReceiptDetailModalOpen(true);
    } else if (action === RECEIPT_ACTIONS.EMAIL) {
      setEmailReceiptModalOpen(true);
      setReceiptId(purchaseId);
    } else if (action === RECEIPT_ACTIONS.REFUND) {
      setIsRefundInterfaceOpen(true);
    }
  };

  const columnConfig = useMemo(
    () => [
      mergeCommonGridColConfig({
        field: 'id',
        headerName: 'Sale ID',
        type: 'number',
        flex: 1.2,
        renderCell: p => p?.id
      }),
      mergeCommonGridColConfig({
        field: 'created_at',
        headerName: 'Purchase Date',
        type: 'date',
        flex: 1.6,
        valueGetter: renderDateTimeFromCol('created_at', 'MM/DD/YYYY')
      }),
      mergeCommonGridColConfig({
        field: 'store',
        headerName: 'Studio',
        type: 'string',
        flex: 2
      }),
      mergeCommonGridColConfig({
        field: 'total_credits_used',
        headerName: 'Credits Used',
        type: 'number',
        flex: 1,
        renderCell: p => p?.row?.total_credits_used || 0
      }),
      mergeCommonGridColConfig({
        field: 'total',
        headerName: 'Total Paid',
        type: 'number',
        flex: 1.1,
        renderCell: p => currencyFormat(p?.row?.total)
      }),
      mergeCommonGridColConfig({
        field: 'dollars_refunded',
        headerName: 'Dollars Refunded',
        type: 'number',
        flex: 1.2,
        sortable: false,
        renderCell: p =>
          p?.row?.dollars_refunded ? (
            <span style={{ color: 'red' }}>
              -{currencyFormat(p?.row?.dollars_refunded)}
            </span>
          ) : (
            '---'
          )
      }),
      mergeCommonGridColConfig({
        field: 'credits_refunded',
        headerName: 'Credits Refunded',
        type: 'number',
        flex: 1.2,
        sortable: false,
        renderCell: p =>
          p?.row?.credits_refunded ? (
            <span style={{ color: 'red' }}>
              -{p?.row?.credits_refunded?.toFixed(0)} credits
            </span>
          ) : (
            '---'
          )
      }),
      mergeCommonGridColConfig({
        field: 'action',
        headerName: 'Action',
        type: 'actions',
        description: 'View, Email or Refund a purchase',
        flex: 1,
        sortable: false,
        headerAlign: 'center',
        align: 'center',
        renderCell: p => (
          <ReceiptActionMenu
            purchaseId={p.id}
            actionCallback={actionCallback}
          />
        )
      })
    ],
    []
  );

  const sortChange = o => {
    setPage(0);
    if (o.length === 0) {
      const currentOptions = sortOptions[0];
      if (currentOptions && typeof currentOptions.field === 'string') {
        const nextOptions: GridSortModel = [
          {
            field: currentOptions.field,
            sort: currentOptions.sort === 'asc' ? 'desc' : 'asc'
          }
        ];
        setSortOptions(nextOptions);
      }
    } else {
      setSortOptions(o);
    }
  };

  return (
    <>
      <FormControlLabel
        labelPlacement="end"
        label={
          <Typography
            variant="body4"
            fontFamily={"'Poppins', sans-serif !important"}
          >
            Only show purchases with refunds
          </Typography>
        }
        control={
          <Checkbox
            color="primary"
            checked={showRefundsOnly}
            onChange={e => setShowRefundsOnly(e?.currentTarget?.checked)}
          />
        }
      />
      <RestoreDataGrid
        rowCount={totalCount}
        columnConfig={columnConfig}
        rows={purchases}
        loading={loading}
        page={page}
        pageSize={pageSize}
        onPageChangeSize={setPageSize}
        onPageChange={setPage}
        sortModel={sortOptions}
        onSortModelChange={sortChange}
      />
      <>
        {isReceiptDetailModalOpen && receiptId && (
          <ReceiptsDetailsModal
            receiptId={receiptId}
            customerEmail={customerAllData?.email}
            customerName={
              customerAllData.firstName + ' ' + customerAllData.lastName
            }
            open={isReceiptDetailModalOpen}
            setModal={setIsReceiptDetailModalOpen}
            setIsRefundInterfaceOpen={setIsRefundInterfaceOpen}
          />
        )}
        {isRefundInterfaceOpen && receiptId && (
          <RefundModal
            open={isRefundInterfaceOpen}
            setIsRefundInterfaceOpen={setIsRefundInterfaceOpen}
            onRefundSuccess={getCustomerPurchases}
            receiptId={receiptId}
          />
        )}
        {emailReceiptModalOpen && receiptId && (
          <EmailReceiptModal
            open={emailReceiptModalOpen}
            onClose={() => setEmailReceiptModalOpen(false)}
            receiptId={receiptId}
            customerEmail={customerAllData?.email}
            customerName={
              customerAllData?.firstName + ' ' + customerAllData?.lastName
            }
          />
        )}
      </>
    </>
  );
};

export default Receipts;
